import { CompanyEventCommonGroupingEntity } from '@nk/data-entities-lib';

export const RISK_SCORE_KEYS = ['very_low', 'low', 'medium', 'high', 'very_high'] as const;
export type RiskScoreKey = (typeof RISK_SCORE_KEYS)[number];

/**
 * Maps each risk score key to its corresponding numeric grouping threshold.
 * Higher numbers indicate lower risk (very_low=4, very_high=0).
 * This inverse relationship allows filtering: a threshold of 2 (medium)
 * includes medium, high, and very_high events.
 */
const RISK_SCORE_MIN_GROUPING: Record<RiskScoreKey, number> = {
  very_low: 4,
  low: 3,
  medium: 2,
  high: 1,
  very_high: 0,
};

/**
 * Default risk score key used when no valid selection is provided.
 */
const DEFAULT_RISK_SCORE_KEY: RiskScoreKey = 'very_low';

/**
 * Finds the appropriate grouping entity based on the selected risk score range.
 *
 * Determines the minimum grouping key from the provided range by selecting
 * the key with the highest numeric threshold value, then finds the matching
 * grouping entity from the list.
 *
 * @param companyEventCommonGroupingList - List of available grouping entities to search
 * @param riskScoreRange - Array of selected risk score keys to consider
 * @returns The matching grouping entity, or undefined if not found
 */
export function resolveCompanyEventCommonGrouping(
  companyEventCommonGroupingList: CompanyEventCommonGroupingEntity[],
  riskScoreRange: RiskScoreKey[],
): CompanyEventCommonGroupingEntity | undefined {
  const minGroupingKey: RiskScoreKey =
    (riskScoreRange || [])
      .slice()
      .sort((a, b) => Math.max(RISK_SCORE_MIN_GROUPING[a], RISK_SCORE_MIN_GROUPING[b]))[0] || DEFAULT_RISK_SCORE_KEY;
  return companyEventCommonGroupingList.find((a) => a.group_key === minGroupingKey);
}

/**
 * Gets all risk score keys that are at or above the specified minimum grouping threshold.
 *
 * For example, if minGroupingKey is 'medium' (threshold=2), this returns
 * ['medium', 'high', 'very_high'] since those have thresholds <= 2.
 *
 * @param minGroupingKey - The minimum risk score key to use as the threshold
 * @returns Array of risk score keys at or above the specified severity level
 */
export function getRiskScoreKeysFromMinGroupingKey(minGroupingKey: RiskScoreKey): RiskScoreKey[] {
  return RISK_SCORE_KEYS.map((key) => key).filter(
    (key) => RISK_SCORE_MIN_GROUPING[minGroupingKey] >= RISK_SCORE_MIN_GROUPING[key],
  );
}
